/**
 * Declarative, **data-only** registry of every upload-vertical dataset's frontend
 * config — the TypeScript mirror of `Dataset::Registry` (app/lib/dataset/registry.rb).
 *
 * One `DatasetConfig` per dataset drives the single parameterized
 * `<DatasetUploadsPage>` (app/frontend/components/dataset-uploads). Everything that
 * differs between the byte-identical pilot pages (master_rental / master_listing) —
 * labels, URLs, channel string, copy, columns, filters, the preview-parser binding —
 * lives here as **pure data**. The component owns all JSX; heavy pages (M4) pass
 * typed render-prop slots from their own thin page module, never via this registry.
 *
 * Milestone 3 registers only the two pilots; M4 fills the rest.
 */

import type { ReactNode } from "react"
import type { SortOption } from "@/components/ui/mobile-sort-sheet"
import { parseMasterRentalForPreview } from "@/lib/masterRentalPreviewParser"
import { parseMasterListingForPreview } from "@/lib/masterListingPreviewParser"
import { parseOutletDistForPreview } from "@/lib/outletDistPreviewParser"
import { parseProductDistForPreview } from "@/lib/productDistPreviewParser"
import { parseStarMasterProductForPreview } from "@/lib/starMasterProductPreviewParser"
import { parseTransDistSoForPreview } from "@/lib/transDistSoPreviewParser"
import { parseTransDistDoForPreview } from "@/lib/transDistDoPreviewParser"
import { parseB2bSellOutForPreview } from "@/lib/b2bSellOutPreviewParser"
import { parseServiceLevelForPreview } from "@/lib/serviceLevelPreviewParser"
import { parseB2bStockForPreview } from "@/lib/stockPreviewParser"
import { parseBpOpscForPreview } from "@/lib/bpOpscPreviewParser"
import { parseB2bSellInForPreview } from "@/lib/b2bSellInPreviewParser"
import { parseMarketShareB2bForPreview } from "@/lib/marketShareB2bPreviewParser"
import { parsePriceMonitoringForPreview } from "@/lib/priceMonitoringPreviewParser"
import { parsePromoActivityForPreview } from "@/lib/promoActivityPreviewParser"
import { parseDiscountPlanForPreview } from "@/lib/discountPlanPreviewParser"
import { parseXlsxForPreview } from "@/lib/xlsxPreviewParser"

// ---------------------------------------------------------------------------
// Monthly-upload key surface — the single TS source (mirrors the Ruby
// registry's `monthly` configs). `inertia.ts` types, `MonthlyUploadAlert`,
// `Dashboard.tsx`, and the `AppShell` nav badges all derive from these.
// ---------------------------------------------------------------------------

export const MONTHLY_CATEGORY_KEYS = [
  "b2b_sell_out",
  "master_rental",
  "master_listing",
  "market_share_b2b",
  "trans_dist_so",
  "trans_dist_do",
  "master_outlet",
  "b2b_service_level",
  "b2b_sell_in",
  "b2b_stock",
  "niq_fragrances",
  "bp_opsc",
] as const

export type MonthlyCategoryKey = (typeof MONTHLY_CATEGORY_KEYS)[number]

/** "bang" → existence/quarterly check (nav badge "!"); "count" → coverage
 *  check (nav badge "uploaded/total"). */
export type MonthlyBadge = "bang" | "count"

export type MonthlyCategory = {
  key: MonthlyCategoryKey
  label: string
  uploadsHref: string
  badge: MonthlyBadge
}

// ---------------------------------------------------------------------------
// Shared row / status types
// ---------------------------------------------------------------------------

export type UploadStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"

/** A history-table row. Known universal fields + dataset-specific extras. */
export type UploadRow = {
  id: number
  filename: string
  status: UploadStatus
  period_label: string
  error_message: string | null
  imported_at: string | null
  created_at: string
  [key: string]: unknown
}

/** A server preview result (or a per-file error). Shape varies per dataset. */
export type PreviewRow = {
  filename: string
  will_replace?: boolean
  is_unchanged?: boolean
  error?: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Column / filter / preview descriptors (pure data)
// ---------------------------------------------------------------------------

export type ColumnFormat =
  | "text"
  | "number"
  | "idr"
  | "number-id"
  | "date-or-created"
  | "primary-sub"
  /** Full Indonesian month name from a 1-based month-number field (bp_opsc "Bulan"). */
  | "month-name"
  /** "{Month} {Year}" composite from `period_month` + `period_year` ("—" when either is missing). */
  | "month-year"

/** "title" → filename (table first col + mobile card title). "status" → status
 *  badge. "field" → a formatted value cell + a mobile DataCardField. */
export type ColumnRole = "title" | "status" | "field"

export type ColumnDef = {
  /** Unique key; doubles as the sort column name when `sortable`. */
  key: string
  /** UploadRow field this column reads. */
  field: string
  /** Desktop table header. */
  label: string
  /** Mobile DataCardField label (defaults to `label`). */
  mobileLabel?: string
  role?: ColumnRole
  format?: ColumnFormat
  /** Secondary field rendered under `field` for the `primary-sub` two-line cell. */
  subField?: string
  align?: "left" | "right"
  sortable?: boolean
  /** Show "—" when the numeric value is null / ≤ 0 (alias for `dash: "zero"`). */
  zeroDash?: boolean
  /** When to render "—": "zero" → null/≤0 (numbers); "null" → only null (keeps 0). */
  dash?: "zero" | "null"
  /** Tailwind max-width utility for the `title` cell (default "max-w-[220px]"). */
  maxWidth?: string
  /** Mobile grid: span two columns. */
  wide?: boolean
  /** CSS-capitalize the cell (b2b report_type values are stored lowercase). */
  capitalize?: boolean
}

export type FilterKind = "year" | "month" | "status" | "search" | "select"

export type FilterDef = {
  kind: FilterKind
  key: string
  /** Mobile sheet field label. */
  label: string
  /** "Semua …" placeholder for selects. */
  allLabel?: string
  /** Search input placeholder. */
  placeholder?: string
  /** Desktop control width utility (e.g. "w-28"). */
  width: string
  /** Which page-prop array supplies a `select`'s options. Any `available_*` prop,
   *  or a dot path into a nested prop (bp_opsc: "filter_options.regions"). */
  optionsFrom?: string
  /** Label numeric options with the full Indonesian month name (bp_opsc month filter). */
  monthNameLabels?: boolean
  /** CSS-capitalize the option labels (b2b report_type select). */
  capitalizeOptions?: boolean
}

export type PreviewMetaField = {
  label: string
  field: string
  format?: ColumnFormat
  /** "truthy" → omit this meta field entirely when the value is falsy. */
  when?: "truthy"
  /** CSS-capitalize the value (b2b report_type). */
  capitalize?: boolean
}

export type PreviewStat = {
  label: string
  /** "This file" value field. */
  field: string
  /** Previous value field (drives the duplicate comparison grid). */
  existingField?: string
  format: ColumnFormat
  /** Label for the inline (non-duplicate) list when it differs from the grid
   *  label (b2b service-level/stock: "Estimasi Record" vs "Jumlah Record"). */
  inlineLabel?: string
}

export type PreviewCardDef = {
  metaFields: PreviewMetaField[]
  stats: PreviewStat[]
}

// ---------------------------------------------------------------------------
// DatasetConfig — one entry per dataset
// ---------------------------------------------------------------------------

export type DatasetConfig = {
  /** Registry key (= `dataset_key` in the Ruby registry). */
  key: string
  /** Human label (page <h1>, MonthlyUploadAlert). */
  label: string
  /** URL base, e.g. "/admin/master-rental/uploads" — all routes derive from it. */
  basePath: string
  /** ActionCable channel class name, e.g. "MasterRentalUploadChannel". */
  channel: string
  /** Monthly-upload category key for <MonthlyUploadAlert>; null → no alert. */
  monthlyKey: MonthlyCategoryKey | null
  /** File-input `accept` attribute. */
  accept: string
  head: { title: string; description: string }
  /** Intro paragraph: `{prefix}<strong>{strong}</strong>{suffix}` (strong "" → plain).
   *  `className` overrides the default "mt-1" (b2b stock/bp_opsc use muted small text). */
  intro: { prefix: string; strong: string; suffix: string; className?: string }
  /** Drop-zone copy. `formatSuffix` trails the `<code>`; `periodFromA1` (default
   *  true) toggles the pilot "Periode dibaca dari sel judul A1" detail line. */
  dropzone: { dragLine: string; formatCode: string; sheetName: string; formatSuffix?: string; periodFromA1?: boolean }
  sortOptions: SortOption[]
  columns: ColumnDef[]
  /** Mobile-card field set when it differs from `columns` (bp_opsc renders a
   *  single "Period" composite where the desktop table has Tahun + Bulan). */
  mobileFields?: ColumnDef[]
  filters: FilterDef[]
  preview: {
    /** Browser-side parser; returns dataset-specific metadata (numbers + strings). */
    parse: (file: File) => Promise<Record<string, unknown>>
    /** Map a parse result (+ filename) into the server `preview` request body item. */
    toMetadata: (filename: string, result: Record<string, unknown>) => Record<string, unknown>
    /** Augment each non-error server preview row with derived display fields
     *  (computed period labels, tracked-panel subtitles) before rendering. */
    derive?: (preview: PreviewRow) => PreviewRow
    /** Server `preview` body item for a file whose browser parse FAILED. Most
     *  datasets drop failed files from the request; bp_opsc sends a
     *  `{filename, row_count: 0}` placeholder. */
    errorMetadata?: (filename: string) => Record<string, unknown>
    card: PreviewCardDef
    /** Show the `stats` inline below the meta fields for a non-duplicate preview
     *  (default true). false → stats appear only in the duplicate comparison grid
     *  (the meta fields already carry the per-file numbers, e.g. star). */
    statsInline?: boolean
    /** Duplicate comparison grid wrapper: default nests the bordered box inside
     *  a `pl-6` padder; the b2b accent pages flatten it into one div with the
     *  border + pl-6 combined. */
    duplicateGridFlat?: boolean
    /** Extra badge in the per-file error card (service-level/stock/sell-in:
     *  "Template Tidak Dikenal"). */
    errorBadge?: string
    /** Extra explanatory line in the per-file error card (market_share:
     *  "File ini tidak bisa diimport. …"). */
    errorDetail?: string
  }
  /** Nullable fields to merge from a status_update onto a row (besides status +
   *  error_message, which are always merged). Pilots: ["row_count"]. */
  liveFields: string[]
  /** Render the "Riwayat Upload" <h2> above the history table (default true). */
  showHistoryHeading?: boolean
  /** Preview field seeding the in-flight ProgressCard subtitle (default "period_label"). */
  trackedSubtitleField?: string
  /** Noun in the ProgressCard "{n} {noun} diimport" line (default "baris"). */
  rowNoun?: string
  /** History-table presentation. "plain" (default; timeseries/trans_dist style):
   *  gray sort arrows, no selected-row tint, in-flight rows show a Cancel button,
   *  bulk-bar "dipilih". "accent" (star/bp_opsc/b2b/market_share style): text-accent
   *  sort arrows + right-justify, bg-accent/5 selected tint, checkbox only when
   *  deletable (no in-table cancel), bulk-bar "terpilih". */
  tableStyle?: "plain" | "accent"
  /** Desktop table wrapper uses overflow-x-auto instead of overflow-hidden. */
  tableScrollX?: boolean
  /** Bulk-bar "{n} {noun}" word. Defaults by tableStyle (plain "dipilih" /
   *  accent "terpilih") but varies independently in the legacy pages —
   *  trans_dist is plain-styled yet says "terpilih". */
  bulkSelectedNoun?: "dipilih" | "terpilih"
  /** AppShell width variant (service-level / market-share use "wide"). */
  shellWidth?: "wide"
  /** Extra classes appended to the three section <h2>s (b2b stock adds
   *  "text-lg font-bold text-ink-display"). */
  headingClass?: string
  /** router.get with preserveState (b2b stock). */
  navigatePreserveState?: boolean
  /** Single-row destroy uses preserveScroll (b2b accent pages). */
  destroyPreserveScroll?: boolean
  /** Single-row destroy keeps the current page param (star/b2b accent pages;
   *  trans_dist drops it). */
  destroyKeepsPage?: boolean
  /** Accent tables normally have no in-table cancel; bp_opsc keeps the plain
   *  style's cancel-or-delete action cell (px-2, Ban for in-flight rows). */
  tableCancelInFlight?: boolean
  /** Header Upload/Pilih Folder buttons stay visible during preview/import,
   *  disabled while busy (bp_opsc). */
  headerButtonsAlwaysVisible?: boolean
  /** "Sedang Diproses" panel sourced from the `in_flight_uploads` page prop
   *  (full active set across pages) with per-row progress + drop-on-terminal
   *  reload — bp_opsc model. Mutually exclusive with features.inFlightPanel. */
  inFlightFromProps?: boolean
  /** Metadata columns rendered inside each in-flight panel row (bp_opsc:
   *  region + month-year period). */
  inFlightFields?: ColumnDef[]
  /** Worker-progress line label (market_share: "Mendeteksi template…"). */
  workerProgressLabel?: string
  /** Server-preview line label (market_share: "Memeriksa data yang sudah ada…"). */
  serverQueryingLabel?: string
  /** Render the server-preview line inline (single flex row) instead of the
   *  centered column (market_share). */
  serverQueryingInline?: boolean
  /** Hide the whole preview panel while the upload POST is in flight
   *  (market_share shows only the history table during the send). */
  previewHiddenWhileImporting?: boolean
  /** Class overrides for the destroy action column (market_share uses
   *  px-4 header + non-right-aligned cells). */
  destroyColumn?: { thClass?: string; tdClass?: string }
  features: {
    /** Separate "Progress Import" tracked-uploads panel (pilots: true). */
    inFlightPanel: boolean
    /** Bulk-select + bulk delete (flag-gated; M4 heavy pages). */
    bulkDelete: boolean
    /** Per-row delete action (flag-gated; M4 heavy pages). */
    destroy: boolean
  }
  /** Confirm copy, used only when `features.destroy` is on (M4). */
  destroyConfirm?: (filename: string) => string
  /** Confirm copy, used only when `features.bulkDelete` is on (M4). */
  bulkDestroyConfirm?: (count: number) => string
}

// ---------------------------------------------------------------------------
// Pure formatters / glue helpers (unit-tested; no JSX, no browser globals)
// ---------------------------------------------------------------------------

export function formatIDR(value: number): string {
  return value.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  })
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

/** Format a value for a preview meta field / stat (no zero-dash logic). */
export function formatValue(format: ColumnFormat | undefined, value: unknown): string {
  switch (format) {
    case "number":
      return Number(value ?? 0).toLocaleString()
    case "number-id":
      return Number(value ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })
    case "idr":
      return formatIDR(Number(value ?? 0))
    case "date-or-created":
      return value ? formatDate(String(value)) : ""
    case "text":
    case undefined:
    default:
      return value == null ? "" : String(value)
  }
}

/** Format a history-table cell, applying the column's dash rule. `dash: "zero"`
 *  (alias `zeroDash`) → null/≤0 render "—"; `dash: "null"` → only null renders "—"
 *  (a literal 0 stays "0"). Mirrors each legacy page's exact rule. The `primary-sub`
 *  composite is rendered as JSX by the component, not here (returns the primary). */
export function formatColumn(col: ColumnDef, row: UploadRow): string {
  const dash = col.dash ?? (col.zeroDash ? "zero" : undefined)
  switch (col.format) {
    case "number":
    case "number-id": {
      // "number-id" mirrors the legacy Intl.NumberFormat("id-ID", {maximumFractionDigits: 0}).
      const fmt = (n: number) =>
        col.format === "number-id" ? n.toLocaleString("id-ID", { maximumFractionDigits: 0 }) : n.toLocaleString()
      if (dash === "null") return row[col.field] == null ? "—" : fmt(Number(row[col.field]))
      const v = Number(row[col.field] ?? 0)
      if (dash === "zero" && !(v > 0)) return "—"
      return fmt(v)
    }
    case "idr": {
      const raw = row[col.field]
      const v = raw == null ? null : Number(raw)
      if (dash === "null" && v == null) return "—"
      if (dash === "zero" && !(v != null && v > 0)) return "—"
      return formatIDR(v ?? 0)
    }
    case "date-or-created": {
      const iso = (row.imported_at as string | null) ?? (row.created_at as string)
      return iso ? formatDate(iso) : ""
    }
    case "primary-sub":
      return row[col.field] == null ? "" : String(row[col.field])
    case "month-name":
      return MONTHS_FULL[Number(row[col.field] ?? 0)] ?? ""
    case "month-year": {
      const m = Number(row.period_month ?? 0)
      const y = row.period_year
      return m && y ? `${MONTHS_FULL[m]} ${y}` : "—"
    }
    case "text":
    default:
      if (dash === "null" && row[col.field] == null) return "—"
      return row[col.field] == null ? "" : String(row[col.field])
  }
}

/** Build the query params for a filter/sort/page navigation — preserves the
 *  configured filter keys + non-default sort/direction/page, then applies the
 *  overrides (a null/""/undefined override deletes the key). */
export function buildNavigateParams(
  filterKeys: string[],
  filters: Record<string, string | null>,
  sort: string,
  direction: string,
  page: number,
  overrides: Record<string, string | number | null>,
): Record<string, string | number> {
  const params: Record<string, string | number> = {}
  for (const k of filterKeys) {
    const v = filters[k]
    if (v) params[k] = v
  }
  if (sort !== "created_at") params.sort = sort
  if (direction !== "desc") params.direction = direction
  if (page > 1) params.page = page
  for (const [k, v] of Object.entries(overrides)) {
    if (v !== null && v !== undefined && v !== "") params[k] = v as string | number
    else delete params[k]
  }
  return params
}

/** A browser-side parse result, tagged with its filename (or a per-file error). */
export type WorkerResult = { filename: string; error?: string; [key: string]: unknown }

/** Map worker parse results (filename-tagged) into the server `preview` body
 *  list. Per-file errors are dropped unless `errorMetadata` supplies a
 *  placeholder body item for them (bp_opsc). */
export function toMetadataList(
  toMetadata: DatasetConfig["preview"]["toMetadata"],
  workerResults: WorkerResult[],
  errorMetadata?: DatasetConfig["preview"]["errorMetadata"],
): Record<string, unknown>[] {
  return workerResults
    .filter((r) => !r.error || errorMetadata)
    .map((r) => {
      if (r.error && errorMetadata) return errorMetadata(r.filename)
      const { filename, error: _error, ...rest } = r
      void _error
      return toMetadata(filename, rest as Record<string, number>)
    })
}

/** The sort option label for the current sort/direction (for the mobile bar). */
export function resolveSortLabel(
  options: SortOption[],
  sort: string,
  direction: string,
): ReactNode {
  return options.find((o) => o.sort === sort && o.direction === direction)?.label ?? "Urutkan"
}

/** Filenames checked by default after a preview: new files (not `will_replace`). */
export function initialCheckedFilenames(previews: PreviewRow[]): Set<string> {
  return new Set(
    previews
      .filter((p) => !p.error && !p.will_replace)
      .map((p) => p.filename),
  )
}

// ---------------------------------------------------------------------------
// Shared option lists (Indonesian month abbreviations + status values)
// ---------------------------------------------------------------------------

export const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"]
export const MONTHS_FULL = [
  "", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
]
export const STATUS_VALUES: UploadStatus[] = ["pending", "processing", "completed", "failed", "cancelled"]

// ---------------------------------------------------------------------------
// Shared building blocks for the "monthly cost" pilots (master_rental/listing)
// ---------------------------------------------------------------------------

const COST_SORT_OPTIONS: SortOption[] = [
  { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
  { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
  { sort: "period", direction: "desc", label: "Periode terbaru" },
  { sort: "period", direction: "asc", label: "Periode terlama" },
  { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
  { sort: "row_count", direction: "asc", label: "Baris paling sedikit" },
  { sort: "total_cost", direction: "desc", label: "Total COST tertinggi" },
  { sort: "total_cost", direction: "asc", label: "Total COST terendah" },
  { sort: "status", direction: "asc", label: "Status A–Z" },
  { sort: "status", direction: "desc", label: "Status Z–A" },
]

const COST_COLUMNS: ColumnDef[] = [
  { key: "filename", field: "filename", label: "File", role: "title" },
  { key: "period", field: "period_label", label: "Periode", format: "text", sortable: true },
  { key: "row_count", field: "row_count", label: "Baris", format: "number", align: "right", sortable: true, zeroDash: true },
  { key: "total_cost", field: "total_cost", label: "Total COST", format: "idr", align: "right", sortable: true, zeroDash: true, wide: true },
  { key: "status", field: "status", label: "Status", role: "status", sortable: true },
  { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
]

const COST_FILTERS: FilterDef[] = [
  { kind: "year", key: "year", label: "Tahun", allLabel: "Semua Tahun", width: "w-28" },
  { kind: "month", key: "month", label: "Bulan", allLabel: "Semua Bulan", width: "w-36" },
  { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
  { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
]

const COST_PREVIEW_CARD: PreviewCardDef = {
  metaFields: [{ label: "Periode", field: "period_label", format: "text" }],
  stats: [
    { label: "Jumlah Baris", field: "row_count", existingField: "existing_row_count", format: "number" },
    { label: "Total COST", field: "total_cost", existingField: "existing_total_cost", format: "idr" },
  ],
}

/** Map a {periodYear, periodMonth, rowCount, totalCost} parse result → preview body. */
function costToMetadata(filename: string, r: Record<string, unknown>): Record<string, unknown> {
  return {
    filename,
    period_year: r.periodYear,
    period_month: r.periodMonth,
    row_count: r.rowCount,
    total_cost: r.totalCost,
  }
}

// ---------------------------------------------------------------------------
// Pilot entries
// ---------------------------------------------------------------------------

export const masterRentalConfig: DatasetConfig = {
  key: "master_rental",
  label: "Master Rental",
  basePath: "/admin/master-rental/uploads",
  channel: "MasterRentalUploadChannel",
  monthlyKey: "master_rental",
  accept: ".xlsx",
  head: {
    title: "Master Rental",
    description:
      "Upload dan kelola data biaya sewa fixture (Rental Cost) bulanan per outlet secara nasional.",
  },
  intro: {
    prefix: "Upload file Excel ",
    strong: "Rental Cost {Tahun} - {Bulan}",
    suffix: " (biaya sewa fixture per outlet).",
  },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih file/folder",
    formatCode: "Rental Cost {Tahun} - {Bulan}.xlsx",
    sheetName: "RENTAL",
  },
  sortOptions: COST_SORT_OPTIONS,
  columns: COST_COLUMNS,
  filters: COST_FILTERS,
  preview: { parse: parseMasterRentalForPreview, toMetadata: costToMetadata, card: COST_PREVIEW_CARD },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: false, destroy: false },
}

export const masterListingConfig: DatasetConfig = {
  key: "master_listing",
  label: "Master Listing",
  basePath: "/admin/master-listing/uploads",
  channel: "MasterListingUploadChannel",
  monthlyKey: "master_listing",
  accept: ".xlsx",
  head: {
    title: "Master Listing",
    description:
      "Upload dan kelola data biaya listing (Listing Cost) bulanan per outlet secara nasional.",
  },
  intro: {
    prefix: "Upload file Excel ",
    strong: "Listing Cost {Tahun} - {Bulan}",
    suffix: " (biaya listing per outlet).",
  },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih file/folder",
    formatCode: "Listing Cost {Tahun} - {Bulan}.xlsx",
    sheetName: "Listing Cost",
  },
  sortOptions: COST_SORT_OPTIONS,
  columns: COST_COLUMNS,
  filters: COST_FILTERS,
  preview: { parse: parseMasterListingForPreview, toMetadata: costToMetadata, card: COST_PREVIEW_CARD },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: false, destroy: false },
}

// ---------------------------------------------------------------------------
// Wave A — distributor-scoped masters (no monthly alert, no destroy, composite
// distributor cell, generic select filter, no "Riwayat Upload" heading).
// ---------------------------------------------------------------------------

export const masterOutletDistConfig: DatasetConfig = {
  key: "master_outlet_dist",
  label: "Master Outlet Dist",
  basePath: "/admin/master-outlet-dist/uploads",
  channel: "MasterOutletDistUploadChannel",
  monthlyKey: null,
  accept: ".xlsx",
  head: {
    title: "Master Outlet Dist",
    description: "Upload dan kelola file Master Outlet Distributor.",
  },
  intro: { prefix: "Upload file Excel outlet per distributor ke database.", strong: "", suffix: "" },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih folder",
    formatCode: "OUTLET_DIST_*.xlsx",
    formatSuffix: " — satu file per distributor",
    sheetName: "OUTLET DISTRIBUTOR",
    periodFromA1: false,
  },
  sortOptions: [
    { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
    { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
    { sort: "dist_name", direction: "asc", label: "Distributor A–Z" },
    { sort: "dist_name", direction: "desc", label: "Distributor Z–A" },
    { sort: "row_count", direction: "desc", label: "Outlet terbanyak" },
    { sort: "row_count", direction: "asc", label: "Outlet paling sedikit" },
    { sort: "replaced_row_count", direction: "desc", label: "Diganti terbanyak" },
    { sort: "replaced_row_count", direction: "asc", label: "Diganti paling sedikit" },
    { sort: "status", direction: "asc", label: "Status A–Z" },
    { sort: "status", direction: "desc", label: "Status Z–A" },
  ],
  columns: [
    { key: "filename", field: "filename", label: "File", role: "title", maxWidth: "max-w-[180px]" },
    { key: "dist_name", field: "dist_name", subField: "dist_sap_code", label: "Distributor", format: "primary-sub", sortable: true, wide: true },
    { key: "row_count", field: "row_count", label: "Jumlah Outlet", format: "number", align: "right", sortable: true, dash: "null" },
    { key: "replaced_row_count", field: "replaced_row_count", label: "Diganti", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
  ],
  filters: [
    { kind: "select", key: "dist_name", label: "Distributor", allLabel: "Semua Distributor", width: "w-56", optionsFrom: "available_dist_names" },
    { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
    { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
  ],
  preview: {
    parse: parseOutletDistForPreview,
    toMetadata: (filename, r) => ({
      filename,
      row_count: r.rowCount,
      dist_sap_code: r.distSapCode,
      dist_name: r.distName,
    }),
    card: {
      metaFields: [
        { label: "Distributor", field: "dist_name", format: "text" },
        { label: "SAP Code", field: "dist_sap_code", format: "text" },
      ],
      stats: [{ label: "Jumlah Outlet", field: "row_count", existingField: "existing_row_count", format: "number" }],
    },
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: false, destroy: false },
  showHistoryHeading: false,
  trackedSubtitleField: "dist_name",
  rowNoun: "outlet",
}

export const masterProductDistConfig: DatasetConfig = {
  key: "master_product_dist",
  label: "Master Product Dist",
  basePath: "/admin/master-product-dist/uploads",
  channel: "MasterProductDistUploadChannel",
  monthlyKey: null,
  accept: ".xlsx",
  head: {
    title: "Master Product Dist",
    description: "Upload dan kelola file Master Product Distributor.",
  },
  intro: { prefix: "Upload file Excel produk per distributor ke database.", strong: "", suffix: "" },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih folder",
    formatCode: "PRODUCT_DIST_*.xlsx",
    formatSuffix: " — satu file per distributor",
    sheetName: "PRODUCT DIST",
    periodFromA1: false,
  },
  sortOptions: [
    { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
    { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
    { sort: "distributor_name", direction: "asc", label: "Distributor A–Z" },
    { sort: "distributor_name", direction: "desc", label: "Distributor Z–A" },
    { sort: "region", direction: "asc", label: "Region A–Z" },
    { sort: "region", direction: "desc", label: "Region Z–A" },
    { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
    { sort: "row_count", direction: "asc", label: "Baris paling sedikit" },
    { sort: "status", direction: "asc", label: "Status A–Z" },
    { sort: "status", direction: "desc", label: "Status Z–A" },
  ],
  columns: [
    { key: "filename", field: "filename", label: "File", role: "title", maxWidth: "max-w-[200px]" },
    { key: "distributor_name", field: "distributor_name", subField: "distributor_sap_code", label: "Distributor", format: "primary-sub", sortable: true, wide: true },
    { key: "region", field: "region", label: "Region", format: "text", sortable: true, dash: "null" },
    { key: "row_count", field: "row_count", label: "Baris", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
  ],
  filters: [
    { kind: "select", key: "region", label: "Region", allLabel: "Semua Region", width: "w-44", optionsFrom: "available_regions" },
    { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
    { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
  ],
  preview: {
    parse: parseProductDistForPreview,
    toMetadata: (filename, r) => ({
      filename,
      row_count: r.rowCount,
      distributor_sap_code: r.distributorSapCode,
      distributor_name: r.distributorName,
      distributor_parent_name: r.distributorParentName,
      region: r.region,
    }),
    card: {
      metaFields: [
        { label: "Distributor", field: "distributor_name", format: "text" },
        { label: "SAP Code", field: "distributor_sap_code", format: "text" },
        { label: "Region", field: "region", format: "text", when: "truthy" },
      ],
      stats: [{ label: "Jumlah Produk", field: "row_count", existingField: "existing_row_count", format: "number" }],
    },
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: false, destroy: false },
  showHistoryHeading: false,
  trackedSubtitleField: "distributor_name",
  rowNoun: "produk",
}

// ---------------------------------------------------------------------------
// Wave B — STAR Master Product (global replace; destroy + bulk; "accent" table
// style — text-accent sort arrows, selected-row tint; dual-sheet dropzone via a
// dropzoneDetail slot; preview meta carries the per-file numbers so non-duplicate
// stats are not repeated inline).
// ---------------------------------------------------------------------------

export const starMasterProductConfig: DatasetConfig = {
  key: "star_master_product",
  label: "Master Product",
  basePath: "/admin/star-master-product/uploads",
  channel: "StarMasterProductUploadChannel",
  monthlyKey: null,
  accept: ".xlsx",
  head: {
    title: "Master Product",
    description: "Upload dan kelola master produk global STAR (PRODUCT ACTIVE & INACTIVE).",
  },
  intro: { prefix: "Upload master produk global STAR ke database.", strong: "", suffix: "" },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih folder",
    formatCode: "PRODUCT (*).xlsx",
    formatSuffix: " — master produk global",
    sheetName: "PRODUCT (ACTIVE)", // unused — dual-sheet detail comes from the dropzoneDetail slot
    periodFromA1: false,
  },
  sortOptions: [
    { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
    { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
    { sort: "active_count", direction: "desc", label: "Active terbanyak" },
    { sort: "active_count", direction: "asc", label: "Active paling sedikit" },
    { sort: "inactive_count", direction: "desc", label: "Inactive terbanyak" },
    { sort: "inactive_count", direction: "asc", label: "Inactive paling sedikit" },
    { sort: "row_count", direction: "desc", label: "Total terbanyak" },
    { sort: "row_count", direction: "asc", label: "Total paling sedikit" },
    { sort: "status", direction: "asc", label: "Status A–Z" },
    { sort: "status", direction: "desc", label: "Status Z–A" },
  ],
  columns: [
    { key: "filename", field: "filename", label: "File", role: "title", maxWidth: "max-w-[240px]" },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "active_count", field: "active_count", label: "Active", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "inactive_count", field: "inactive_count", label: "Inactive", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "row_count", field: "row_count", label: "Total", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true },
  ],
  filters: [
    { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
    { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
  ],
  preview: {
    parse: parseStarMasterProductForPreview,
    toMetadata: (filename, r) => ({
      filename,
      active_count: r.activeCount,
      inactive_count: r.inactiveCount,
      total: r.total,
    }),
    card: {
      metaFields: [
        { label: "Active", field: "active_count", format: "number" },
        { label: "Inactive", field: "inactive_count", format: "number" },
        { label: "Total", field: "total", format: "number" },
      ],
      stats: [{ label: "Total Produk", field: "total", existingField: "existing_row_count", format: "number" }],
    },
    statsInline: false,
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: true, destroy: true },
  tableStyle: "accent",
  tableScrollX: true,
  rowNoun: "produk",
  destroyConfirm: (filename) =>
    `Hapus upload "${filename}"?\n\nSemua baris produk yang diimport dari file ini akan ikut terhapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua baris produk dari file-file ini juga akan terhapus.`,
}

// ---------------------------------------------------------------------------
// Wave C — distributor transaction CSVs (trans_dist_so / trans_dist_do). Plain
// table style with destroy + bulk, inline-history live mode (no tracked panel),
// .csv accept, weekly-file periods. SO and DO differ only in copy: the net-value
// column is "Value Net" (SO) vs "Netto" (DO).
// ---------------------------------------------------------------------------

function transDistSortOptions(nettoSortLabel: string): SortOption[] {
  return [
    { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
    { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
    { sort: "period", direction: "desc", label: "Periode terbaru" },
    { sort: "period", direction: "asc", label: "Periode terlama" },
    { sort: "distributor_name", direction: "asc", label: "Distributor A–Z" },
    { sort: "distributor_name", direction: "desc", label: "Distributor Z–A" },
    { sort: "region_name", direction: "asc", label: "Region A–Z" },
    { sort: "region_name", direction: "desc", label: "Region Z–A" },
    { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
    { sort: "row_count", direction: "asc", label: "Baris paling sedikit" },
    { sort: "value_net_sum", direction: "desc", label: `${nettoSortLabel} tertinggi` },
    { sort: "value_net_sum", direction: "asc", label: `${nettoSortLabel} terendah` },
    { sort: "status", direction: "asc", label: "Status A–Z" },
    { sort: "status", direction: "desc", label: "Status Z–A" },
  ]
}

function transDistColumns(nettoLabel: string): ColumnDef[] {
  return [
    { key: "filename", field: "filename", label: "File", role: "title", maxWidth: "max-w-[200px]" },
    { key: "distributor_name", field: "distributor_name", label: "Distributor", format: "text", sortable: true },
    { key: "region_name", field: "region_name", label: "Region", format: "text", sortable: true },
    { key: "period", field: "period_label", label: "Periode", format: "text", sortable: true },
    { key: "row_count", field: "row_count", label: "Baris", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "value_net_sum", field: "value_net_sum", label: nettoLabel, format: "idr", align: "right", sortable: true, dash: "null", wide: true },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
  ]
}

function transDistPreviewCard(nettoLabel: string): PreviewCardDef {
  return {
    metaFields: [
      { label: "Distributor", field: "distributor_name", format: "text" },
      { label: "Region", field: "region_name", format: "text" },
      { label: "Periode", field: "period_label", format: "text" },
    ],
    stats: [
      { label: "Jumlah Baris", field: "row_count", existingField: "existing_row_count", format: "number" },
      { label: nettoLabel, field: "value_net_sum", existingField: "existing_value_net_sum", format: "idr" },
    ],
  }
}

/** Map a {rowCount, valueNetSum} parse result → preview body. */
function transDistToMetadata(filename: string, r: Record<string, unknown>): Record<string, unknown> {
  return { filename, row_count: r.rowCount, value_net_sum: r.valueNetSum }
}

const TRANS_DIST_SHARED = {
  accept: ".csv",
  intro: { prefix: "Upload file CSV ", strong: "{Distributor}, {Region} - {YYYY}-{MM}-Week {W}" },
  dropzone: {
    dragLine: "Drag & drop file .csv ke sini, atau pilih file/folder",
    formatCode: "{Distributor Name}, {Region Name} - {YYYY}-{MM}-Week {W} ({timestamp}).csv",
    sheetName: "", // unused — the semicolon/column detail line comes from the dropzoneDetail slot
    periodFromA1: false,
  },
  filters: COST_FILTERS,
  liveFields: ["row_count"],
  features: { inFlightPanel: false, bulkDelete: true, destroy: true },
  bulkSelectedNoun: "terpilih" as const,
  destroyConfirm: (filename: string) =>
    `Hapus "${filename}"?\n\nSemua data transaksi untuk periode ini juga akan dihapus.`,
  bulkDestroyConfirm: (count: number) =>
    `Hapus ${count} upload terpilih?\n\nSemua data transaksi untuk periode-periode ini juga akan dihapus.`,
}

export const transDistSoConfig: DatasetConfig = {
  key: "trans_dist_so",
  label: "Trans Dist SO",
  basePath: "/admin/trans-dist-so/uploads",
  channel: "TransDistSoUploadChannel",
  monthlyKey: "trans_dist_so",
  accept: TRANS_DIST_SHARED.accept,
  head: {
    title: "Trans Dist SO",
    description: "Upload dan kelola data Sales Order (SO) Distributor bulanan.",
  },
  intro: { ...TRANS_DIST_SHARED.intro, suffix: " (Sales Order distributor)." },
  dropzone: TRANS_DIST_SHARED.dropzone,
  sortOptions: transDistSortOptions("Value Net"),
  columns: transDistColumns("Total Value Net"),
  filters: TRANS_DIST_SHARED.filters,
  preview: { parse: parseTransDistSoForPreview, toMetadata: transDistToMetadata, card: transDistPreviewCard("Total Value Net") },
  liveFields: TRANS_DIST_SHARED.liveFields,
  features: TRANS_DIST_SHARED.features,
  bulkSelectedNoun: TRANS_DIST_SHARED.bulkSelectedNoun,
  destroyConfirm: TRANS_DIST_SHARED.destroyConfirm,
  bulkDestroyConfirm: TRANS_DIST_SHARED.bulkDestroyConfirm,
}

export const transDistDoConfig: DatasetConfig = {
  key: "trans_dist_do",
  label: "Trans Dist DO",
  basePath: "/admin/trans-dist-do/uploads",
  channel: "TransDistDoUploadChannel",
  monthlyKey: "trans_dist_do",
  accept: TRANS_DIST_SHARED.accept,
  head: {
    title: "Trans Dist DO",
    description: "Upload dan kelola data Delivery Order (DO) Distributor bulanan.",
  },
  intro: { ...TRANS_DIST_SHARED.intro, suffix: " (Delivery Order distributor)." },
  dropzone: TRANS_DIST_SHARED.dropzone,
  sortOptions: transDistSortOptions("Netto"),
  columns: transDistColumns("Total Netto"),
  filters: TRANS_DIST_SHARED.filters,
  preview: { parse: parseTransDistDoForPreview, toMetadata: transDistToMetadata, card: transDistPreviewCard("Total Netto") },
  liveFields: TRANS_DIST_SHARED.liveFields,
  features: TRANS_DIST_SHARED.features,
  bulkSelectedNoun: TRANS_DIST_SHARED.bulkSelectedNoun,
  destroyConfirm: TRANS_DIST_SHARED.destroyConfirm,
  bulkDestroyConfirm: TRANS_DIST_SHARED.bulkDestroyConfirm,
}

// ---------------------------------------------------------------------------
// Wave D — b2b_sell_out (plain, non-destroy, tracked panel) + the accent
// destroy/bulk pages b2b_service_level / b2b_stock (shared SL shape) and
// bp_opsc (prop-sourced "Sedang Diproses" in-flight panel).
// ---------------------------------------------------------------------------

export const b2bSellOutConfig: DatasetConfig = {
  key: "b2b_sell_out",
  label: "B2B - Sell Out",
  basePath: "/admin/b2b-sell-out/uploads",
  channel: "B2bSellOutUploadChannel",
  monthlyKey: "b2b_sell_out",
  accept: ".xls,.xlsx",
  head: {
    title: "B2B - Sell Out",
    description: "Upload dan kelola file B2B - Sell Out per distributor.",
  },
  intro: { prefix: "Upload file Excel sell-out per account (IDM, IDG, MIDI, SAT).", strong: "", suffix: "" },
  dropzone: {
    dragLine: "Drag & drop file .xls atau .xlsx ke sini, atau pilih folder",
    formatCode: "Report Time Series (Regular) - Distributor ({Nama}, Indonesia) - {YYYY}-{MM}_....xls[x]",
    sheetName: "Report Time Series",
    periodFromA1: false,
  },
  sortOptions: [
    { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
    { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
    { sort: "distributor_code", direction: "asc", label: "Account A–Z" },
    { sort: "distributor_code", direction: "desc", label: "Account Z–A" },
    { sort: "period", direction: "desc", label: "Periode terbaru" },
    { sort: "period", direction: "asc", label: "Periode terlama" },
    { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
    { sort: "row_count", direction: "asc", label: "Baris paling sedikit" },
    { sort: "netto_wise_sum", direction: "desc", label: "Netto Wise tertinggi" },
    { sort: "netto_wise_sum", direction: "asc", label: "Netto Wise terendah" },
    { sort: "status", direction: "asc", label: "Status A–Z" },
    { sort: "status", direction: "desc", label: "Status Z–A" },
  ],
  columns: [
    { key: "filename", field: "filename", label: "File", role: "title", maxWidth: "max-w-[180px]" },
    { key: "distributor_code", field: "distributor_code", subField: "distributor_name", label: "Account", format: "primary-sub", sortable: true, wide: true },
    { key: "period", field: "period_label", label: "Periode", format: "text", sortable: true },
    { key: "row_count", field: "row_count", label: "Baris", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "netto_wise_sum", field: "netto_wise_sum", label: "Netto Wise", format: "idr", align: "right", sortable: true, dash: "null", wide: true },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
  ],
  filters: [
    { kind: "select", key: "distributor_code", label: "Account", allLabel: "Semua Account", width: "w-36", optionsFrom: "available_distributor_codes" },
    { kind: "year", key: "year", label: "Tahun", allLabel: "Semua Tahun", width: "w-28" },
    { kind: "month", key: "month", label: "Bulan", allLabel: "Semua Bulan", width: "w-36" },
    { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
    { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
  ],
  preview: {
    parse: parseB2bSellOutForPreview as unknown as (file: File) => Promise<Record<string, unknown>>,
    toMetadata: (filename, r) => ({
      filename,
      row_count: r.rowCount,
      netto_wise_sum: r.nettoSum,
      distributor_code: r.distributorCode,
    }),
    // The legacy page computes the preview/tracked period label client-side
    // from period_year+month via toLocaleDateString — reproduced verbatim.
    derive: (p) => {
      const periodLabel =
        p.period_year != null && p.period_month != null
          ? new Date(Number(p.period_year), Number(p.period_month) - 1, 1).toLocaleDateString("id-ID", {
              month: "short",
              year: "numeric",
            })
          : ""
      // Bug B1 (#7): a .xls is counted by the server during import (the browser can't
      // unzip it), so show "Dihitung server (.xls)" in place of the browser-unknown
      // row count + netto — the same display the b2b stock/sell-in uploaders use.
      const isXls = String(p.filename).toLowerCase().endsWith(".xls")
      return {
        ...p,
        period_label_display: periodLabel,
        tracked_subtitle: [p.distributor_code, periodLabel].filter(Boolean).join(" · "),
        row_count_display: isXls ? "Dihitung server (.xls)" : Number(p.row_count ?? 0).toLocaleString("id-ID"),
        netto_wise_sum_display: isXls ? "Dihitung server (.xls)" : formatIDR(Number(p.netto_wise_sum ?? 0)),
        existing_row_count_display: p.existing_row_count != null ? Number(p.existing_row_count).toLocaleString("id-ID") : null,
        existing_netto_wise_sum_display: p.existing_netto_wise_sum != null ? formatIDR(Number(p.existing_netto_wise_sum)) : null,
      }
    },
    card: {
      metaFields: [
        { label: "Distributor", field: "distributor_name", format: "text" },
        { label: "Kode", field: "distributor_code", format: "text" },
        { label: "Periode", field: "period_label_display", format: "text" },
      ],
      stats: [
        { label: "Jumlah Baris", inlineLabel: "Estimasi Baris", field: "row_count_display", existingField: "existing_row_count_display", format: "text" },
        { label: "Netto Wise", field: "netto_wise_sum_display", existingField: "existing_netto_wise_sum_display", format: "text" },
      ],
    },
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: false, destroy: false },
  trackedSubtitleField: "tracked_subtitle",
}

// --- b2b_service_level / b2b_stock: the shared accent SL shape -------------

function b2bSlColumns(): ColumnDef[] {
  return [
    { key: "filename", field: "filename", label: "File", role: "title", maxWidth: "max-w-[200px]" },
    { key: "account_code", field: "account_code", subField: "account_name", label: "Account", format: "primary-sub", sortable: true, wide: true },
    { key: "report_type", field: "report_type", label: "Tipe", format: "text", sortable: true, capitalize: true },
    { key: "period", field: "period_label", label: "Periode", format: "text", sortable: true },
    { key: "row_count", field: "row_count", label: "Baris", format: "number", align: "right", sortable: true, dash: "null" },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
  ]
}

const B2B_SL_SORT_OPTIONS: SortOption[] = [
  { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
  { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
  { sort: "account_code", direction: "asc", label: "Account A–Z" },
  { sort: "account_code", direction: "desc", label: "Account Z–A" },
  { sort: "report_type", direction: "asc", label: "Tipe A–Z" },
  { sort: "report_type", direction: "desc", label: "Tipe Z–A" },
  { sort: "period", direction: "desc", label: "Periode terbaru" },
  { sort: "period", direction: "asc", label: "Periode terlama" },
  { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
  { sort: "row_count", direction: "asc", label: "Baris paling sedikit" },
  { sort: "status", direction: "asc", label: "Status A–Z" },
  { sort: "status", direction: "desc", label: "Status Z–A" },
]

const B2B_SL_FILTERS: FilterDef[] = [
  { kind: "select", key: "account_code", label: "Account", allLabel: "Semua Account", width: "w-32", optionsFrom: "available_account_codes" },
  { kind: "select", key: "report_type", label: "Tipe", allLabel: "Semua Tipe", width: "w-36", optionsFrom: "available_report_types", capitalizeOptions: true },
  { kind: "year", key: "year", label: "Tahun", allLabel: "Semua Tahun", width: "w-28" },
  { kind: "month", key: "month", label: "Bulan", allLabel: "Semua Bulan", width: "w-36" },
  { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
  { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
]

/** The SL-family parsers return `{unknown: true, reason}` instead of throwing;
 *  map that onto the component's error path so the reason reaches the error card. */
function slParseAdapter(
  parse: (file: File) => Promise<Record<string, unknown>>,
): (file: File) => Promise<Record<string, unknown>> {
  return async (file) => {
    const r = await parse(file)
    if (r.unknown) throw new Error(String(r.reason ?? "Template tidak dikenal"))
    return r
  }
}

function slToMetadata(filename: string, r: Record<string, unknown>): Record<string, unknown> {
  return {
    filename,
    account_code: r.accountCode,
    account_name: r.accountName,
    report_type: r.reportType,
    template_version: r.templateVersion,
    period_year: r.periodYear,
    period_month: r.periodMonth,
    row_count: r.rowCount,
  }
}

function slDerive(p: PreviewRow): PreviewRow {
  const periodLabel = `${MONTHS[Number(p.period_month ?? 0)] ?? ""} ${p.period_year ?? ""}`.trim()
  return {
    ...p,
    period_label_display: periodLabel,
    tracked_subtitle: [p.account_code, p.report_type, periodLabel].filter(Boolean).join(" · "),
  }
}

const B2B_SL_META: PreviewMetaField[] = [
  { label: "Account", field: "account_code", format: "text" },
  { label: "Nama", field: "account_name", format: "text" },
  { label: "Tipe", field: "report_type", format: "text", capitalize: true },
  { label: "Periode", field: "period_label_display", format: "text" },
]

export const b2bServiceLevelConfig: DatasetConfig = {
  key: "b2b_service_level",
  label: "B2B - Service Level",
  basePath: "/admin/b2b/service-level",
  channel: "B2bServiceLevelUploadChannel",
  monthlyKey: "b2b_service_level",
  accept: ".xlsx",
  head: {
    title: "B2B - Service Level",
    description:
      "Modul data Service Level untuk mengukur performa ketepatan pemenuhan pesanan (CFR, OFR, OTIF) distributor.",
  },
  intro: { prefix: "Upload dan kelola data Service Level distributor bulanan.", strong: "", suffix: "" },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih file/folder",
    formatCode: "", // unused — the supported-templates line comes from the dropzoneDetail slot
    sheetName: "",
    periodFromA1: false,
  },
  sortOptions: B2B_SL_SORT_OPTIONS,
  columns: b2bSlColumns(),
  filters: B2B_SL_FILTERS,
  preview: {
    parse: slParseAdapter(parseServiceLevelForPreview as unknown as (file: File) => Promise<Record<string, unknown>>),
    toMetadata: slToMetadata,
    derive: slDerive,
    card: {
      metaFields: B2B_SL_META,
      stats: [
        { label: "Jumlah Record", inlineLabel: "Estimasi Record", field: "row_count", existingField: "existing_count", format: "number" },
      ],
    },
    duplicateGridFlat: true,
    errorBadge: "Template Tidak Dikenal",
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: true, destroy: true },
  tableStyle: "accent",
  tableScrollX: true,
  shellWidth: "wide",
  rowNoun: "record",
  trackedSubtitleField: "tracked_subtitle",
  destroyPreserveScroll: true,
  destroyKeepsPage: true,
  destroyConfirm: (filename) =>
    `Hapus upload "${filename}"?\n\nSemua record yang diimport dari file ini akan ikut terhapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua data transaksi untuk periode-periode ini juga akan terhapus.`,
}

export const b2bStockConfig: DatasetConfig = {
  key: "b2b_stock",
  label: "B2B - Stock",
  basePath: "/admin/b2b/stock",
  channel: "B2bStockUploadChannel",
  monthlyKey: "b2b_stock",
  accept: ".xls,.xlsx",
  head: {
    title: "B2B - Stock",
    description:
      "Upload dan kelola data stok periodik (DC & Store) lintas akun ritel — Indogrosir, Indomaret, Alfamidi, Alfamart — untuk memantau tingkat persediaan.",
  },
  intro: {
    prefix: "Upload dan kelola data stok periodik (DC & Store) per distributor.",
    strong: "",
    suffix: "",
    className: "mt-1 text-sm text-ink-muted",
  },
  dropzone: {
    dragLine: "Drag & drop file .xls atau .xlsx ke sini, atau pilih file/folder",
    formatCode: "", // unused — the supported-templates line comes from the dropzoneDetail slot
    sheetName: "",
    periodFromA1: false,
  },
  sortOptions: B2B_SL_SORT_OPTIONS,
  columns: b2bSlColumns(),
  filters: B2B_SL_FILTERS,
  preview: {
    parse: slParseAdapter(parseB2bStockForPreview as unknown as (file: File) => Promise<Record<string, unknown>>),
    toMetadata: slToMetadata,
    // .xls files can't be counted in the browser — the legacy page renders
    // "Dihitung server (.xls)" in place of the row count, so the display
    // values are derived as pre-formatted strings.
    derive: (p) => {
      const base = slDerive(p)
      const isXls = String(p.filename).toLowerCase().endsWith(".xls")
      return {
        ...base,
        row_count_display: isXls ? "Dihitung server (.xls)" : Number(p.row_count ?? 0).toLocaleString(),
        existing_count_display: p.existing_count != null ? Number(p.existing_count).toLocaleString() : null,
      }
    },
    card: {
      metaFields: B2B_SL_META,
      stats: [
        { label: "Jumlah Record", inlineLabel: "Estimasi Record", field: "row_count_display", existingField: "existing_count_display", format: "text" },
      ],
    },
    duplicateGridFlat: true,
    errorBadge: "Template Tidak Dikenal",
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: true, destroy: true },
  tableStyle: "accent",
  tableScrollX: true,
  headingClass: "text-lg font-bold text-ink-display",
  navigatePreserveState: true,
  rowNoun: "record",
  trackedSubtitleField: "tracked_subtitle",
  destroyPreserveScroll: true,
  destroyKeepsPage: true,
  destroyConfirm: (filename) =>
    `Hapus upload "${filename}"?\n\nSemua record stok yang diimport dari file ini akan ikut terhapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua data stok untuk periode-periode ini juga akan terhapus.`,
}

export const bpOpscConfig: DatasetConfig = {
  key: "bp_opsc",
  label: "BP OPSC",
  basePath: "/admin/beauty-promoters/bp-opsc/uploads",
  channel: "BpOpscUploadChannel",
  monthlyKey: null, // monthly-tracked in the checker, but the page shows no alert
  accept: ".xlsx",
  head: {
    title: "BP OPSC",
    description: "Upload dan kelola data OPSC (Operational Stock & Sales Control) untuk Beauty Promoters.",
  },
  intro: {
    prefix: "Upload dan kelola data OPSC (Operational Stock & Sales Control) untuk Beauty Promoters.",
    strong: "",
    suffix: "",
    className: "mt-1 text-sm text-ink-muted",
  },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih folder",
    formatCode: "Report OPSC - BP (Regular) - Region (...) - YYYY-MM_....xlsx",
    sheetName: "", // unused — the region list line comes from the dropzoneDetail slot
    periodFromA1: false,
  },
  sortOptions: [
    { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
    { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
    { sort: "region", direction: "asc", label: "Region A–Z" },
    { sort: "region", direction: "desc", label: "Region Z–A" },
    { sort: "period_year", direction: "desc", label: "Tahun terbaru" },
    { sort: "period_year", direction: "asc", label: "Tahun terlama" },
    { sort: "period_month", direction: "desc", label: "Bulan terbaru" },
    { sort: "period_month", direction: "asc", label: "Bulan terlama" },
    { sort: "row_count", direction: "desc", label: "Row terbanyak" },
    { sort: "row_count", direction: "asc", label: "Row paling sedikit" },
    { sort: "status", direction: "asc", label: "Status A–Z" },
    { sort: "status", direction: "desc", label: "Status Z–A" },
  ],
  columns: [
    { key: "filename", field: "filename", label: "File", role: "title", maxWidth: "max-w-[240px]" },
    { key: "region", field: "region", label: "Region", format: "text", sortable: true },
    { key: "period_year", field: "period_year", label: "Tahun", format: "text", sortable: true },
    { key: "period_month", field: "period_month", label: "Bulan", format: "month-name", sortable: true },
    { key: "row_count", field: "row_count", label: "Rows", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", format: "date-or-created", sortable: true },
  ],
  // Mobile cards collapse Tahun+Bulan into one "Period" composite.
  mobileFields: [
    { key: "region", field: "region", label: "Region", format: "text" },
    { key: "period", field: "period_month", label: "Period", format: "month-year" },
    { key: "row_count", field: "row_count", label: "Rows", format: "number", zeroDash: true },
    { key: "created_at", field: "created_at", label: "Diunggah", format: "date-or-created" },
  ],
  filters: [
    { kind: "select", key: "region", label: "Region", allLabel: "Semua Region", width: "w-40", optionsFrom: "filter_options.regions" },
    { kind: "select", key: "year", label: "Tahun", allLabel: "Semua Tahun", width: "w-28", optionsFrom: "filter_options.years" },
    { kind: "select", key: "month", label: "Bulan", allLabel: "Semua Bulan", width: "w-32", optionsFrom: "filter_options.months", monthNameLabels: true },
    { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
    { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
  ],
  preview: {
    parse: parseBpOpscForPreview as unknown as (file: File) => Promise<Record<string, unknown>>,
    toMetadata: (filename, r) => ({
      filename,
      region: r.region,
      period_year: r.periodYear,
      period_month: r.periodMonth,
      month_range_start: r.monthRangeStart,
      month_range_end: r.monthRangeEnd,
      row_count: r.rowCount,
    }),
    errorMetadata: (filename) => ({ filename, row_count: 0 }),
    derive: (p) => ({
      ...p,
      region_display: p.region ?? "—",
      period_label_display:
        p.period_month && p.period_year ? `${MONTHS_FULL[Number(p.period_month)]} ${p.period_year}` : "—",
      range_display:
        p.month_range_start && p.month_range_end ? `${p.month_range_start} – ${p.month_range_end}` : "—",
    }),
    card: {
      metaFields: [
        { label: "Region", field: "region_display", format: "text" },
        { label: "Periode", field: "period_label_display", format: "text" },
        { label: "6MM", field: "range_display", format: "text" },
      ],
      stats: [{ label: "Rows", field: "row_count", existingField: "existing_row_count", format: "number" }],
    },
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: false, bulkDelete: true, destroy: true },
  tableStyle: "accent",
  tableScrollX: true,
  tableCancelInFlight: true,
  headerButtonsAlwaysVisible: true,
  inFlightFromProps: true,
  inFlightFields: [
    { key: "region", field: "region", label: "Region", format: "text" },
    { key: "period", field: "period_month", label: "Period", format: "month-year" },
  ],
  destroyPreserveScroll: true,
  destroyKeepsPage: true,
  destroyConfirm: (filename) =>
    `Hapus upload "${filename}"?\n\nSemua baris data yang diimport dari file ini akan ikut terhapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua baris data dari file-file ini juga akan terhapus.`,
}

// ---------------------------------------------------------------------------
// Wave E — the heavy slot pages. b2b_sell_in is the stock-variant of the SL
// shape; market_share_b2b adds period-range labels + its own preview-flow copy;
// timeseries is the inline-history page (no panel, no monthly alert) with the
// Data-Integrity banner passed via the headerBanner slot from its thin module.
// ---------------------------------------------------------------------------

export const b2bSellInConfig: DatasetConfig = {
  key: "b2b_sell_in",
  label: "B2B - Sell In",
  basePath: "/admin/b2b/sell-in",
  channel: "B2bSellInUploadChannel",
  monthlyKey: "b2b_sell_in",
  accept: ".xls,.xlsx",
  head: {
    title: "B2B - Sell In",
    description:
      "Modul data Sell In dari pabrik/prinsipal ke distributor untuk memantau performa pasokan produk secara terpusat.",
  },
  intro: {
    prefix: "Upload dan kelola data transaksi Sell In dari prinsipal ke distributor.",
    strong: "",
    suffix: "",
    className: "mt-1 text-sm text-ink-muted",
  },
  dropzone: {
    dragLine: "Drag & drop file .xls atau .xlsx ke sini, atau pilih file/folder",
    formatCode: "", // unused — the supported-templates line comes from the dropzoneDetail slot
    sheetName: "",
    periodFromA1: false,
  },
  sortOptions: B2B_SL_SORT_OPTIONS,
  columns: b2bSlColumns(),
  filters: B2B_SL_FILTERS,
  preview: {
    parse: slParseAdapter(parseB2bSellInForPreview as unknown as (file: File) => Promise<Record<string, unknown>>),
    toMetadata: slToMetadata,
    derive: (p) => {
      const base = slDerive(p)
      const isXls = String(p.filename).toLowerCase().endsWith(".xls")
      return {
        ...base,
        row_count_display: isXls ? "Dihitung server (.xls)" : Number(p.row_count ?? 0).toLocaleString(),
        existing_count_display: p.existing_count != null ? Number(p.existing_count).toLocaleString() : null,
      }
    },
    card: {
      metaFields: B2B_SL_META,
      stats: [
        { label: "Jumlah Record", inlineLabel: "Estimasi Record", field: "row_count_display", existingField: "existing_count_display", format: "text" },
      ],
    },
    duplicateGridFlat: true,
    errorBadge: "Template Tidak Dikenal",
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: true, destroy: true },
  tableStyle: "accent",
  tableScrollX: true,
  headingClass: "text-lg font-bold text-ink-display",
  rowNoun: "record",
  trackedSubtitleField: "tracked_subtitle",
  destroyPreserveScroll: true,
  destroyKeepsPage: true,
  destroyConfirm: (filename) =>
    `Hapus upload "${filename}"?\n\nSemua record yang diimport dari file ini akan ikut terhapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua data transaksi untuk periode-periode ini juga akan terhapus.`,
}

export const marketShareB2bConfig: DatasetConfig = {
  key: "market_share_b2b",
  label: "B2B - Market Share",
  basePath: "/admin/market-share-b2b/uploads",
  channel: "MarketShareB2bUploadChannel",
  monthlyKey: "market_share_b2b",
  accept: ".xlsx",
  head: {
    title: "B2B - Market Share",
    description: "Upload dan kelola file B2B - Market Share dari berbagai account (IDG, IDM, MIDI, SAT).",
  },
  intro: { prefix: "Upload file Excel market share dari IDG, IDM, MIDI, dan SAT.", strong: "", suffix: "" },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih folder",
    formatCode: "", // unused — the supported-templates line comes from the dropzoneDetail slot
    sheetName: "",
    periodFromA1: false,
  },
  sortOptions: B2B_SL_SORT_OPTIONS,
  columns: b2bSlColumns(),
  filters: B2B_SL_FILTERS,
  preview: {
    parse: slParseAdapter(parseMarketShareB2bForPreview as unknown as (file: File) => Promise<Record<string, unknown>>),
    toMetadata: (filename, r) => ({
      filename,
      account_code: r.accountCode,
      account_name: r.accountName,
      report_type: r.reportType,
      template_version: r.templateVersion,
      period_year_from: r.periodYearFrom,
      period_month_from: r.periodMonthFrom,
      period_year_to: r.periodYearTo,
      period_month_to: r.periodMonthTo,
      row_count: r.rowCount,
    }),
    derive: (p) => {
      const from = `${MONTHS[Number(p.period_month_from ?? 0)] ?? ""} ${p.period_year_from ?? ""}`.trim()
      const to = `${MONTHS[Number(p.period_month_to ?? 0)] ?? ""} ${p.period_year_to ?? ""}`.trim()
      const periodLabel = from === to ? from : `${from} – ${to}`
      return {
        ...p,
        period_label_display: periodLabel,
        tracked_subtitle: [p.account_code, p.report_type, periodLabel].filter(Boolean).join(" · "),
      }
    },
    card: {
      metaFields: B2B_SL_META,
      stats: [
        { label: "Jumlah Record", inlineLabel: "Estimasi Record", field: "row_count", existingField: "existing_count", format: "number" },
      ],
    },
    duplicateGridFlat: true,
    errorBadge: "Template Tidak Dikenal",
    errorDetail: "File ini tidak bisa diimport. Parser baru harus ditambahkan di code.",
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: true, destroy: true },
  tableStyle: "accent",
  tableScrollX: true,
  shellWidth: "wide",
  rowNoun: "record",
  trackedSubtitleField: "tracked_subtitle",
  workerProgressLabel: "Mendeteksi template…",
  serverQueryingLabel: "Memeriksa data yang sudah ada…",
  serverQueryingInline: true,
  previewHiddenWhileImporting: true,
  destroyColumn: { thClass: "px-4 py-2.5", tdClass: "px-4 py-3" },
  destroyPreserveScroll: true,
  destroyKeepsPage: true,
  destroyConfirm: (filename) =>
    `Hapus upload "${filename}"?\n\nSemua record yang diimport dari file ini akan ikut terhapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua data transaksi untuk periode-periode ini juga akan terhapus.`,
}

export const timeseriesConfig: DatasetConfig = {
  key: "timeseries",
  label: "Timeseries Uploads",
  basePath: "/admin/timeseries/uploads",
  channel: "TimeseriesUploadChannel",
  monthlyKey: null, // monthly-tracked in the checker, but the page shows no alert
  accept: ".xlsx",
  head: {
    title: "Timeseries Uploads",
    description: "Upload and manage timeseries Excel files.",
  },
  intro: { prefix: "Upload file Excel bulanan per region ke database.", strong: "", suffix: "" },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih folder",
    formatCode: "Report Time Series (Regular) - Region (...) - YYYY-MM_....xlsx",
    sheetName: "", // unused — the region list line comes from the dropzoneDetail slot
    periodFromA1: false,
  },
  sortOptions: [
    { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
    { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
    { sort: "region", direction: "asc", label: "Region A–Z" },
    { sort: "region", direction: "desc", label: "Region Z–A" },
    { sort: "period", direction: "desc", label: "Periode terbaru" },
    { sort: "period", direction: "asc", label: "Periode terlama" },
    { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
    { sort: "row_count", direction: "asc", label: "Baris paling sedikit" },
    { sort: "netto_wise_sum", direction: "desc", label: "Netto Wise tertinggi" },
    { sort: "netto_wise_sum", direction: "asc", label: "Netto Wise terendah" },
    { sort: "status", direction: "asc", label: "Status A–Z" },
    { sort: "status", direction: "desc", label: "Status Z–A" },
  ],
  columns: [
    { key: "filename", field: "filename", label: "File", role: "title", maxWidth: "max-w-[200px]" },
    { key: "region", field: "region", label: "Region", format: "text", sortable: true },
    { key: "period", field: "period_label", label: "Periode", format: "text", sortable: true },
    { key: "row_count", field: "row_count", label: "Baris", format: "number", align: "right", sortable: true, dash: "null" },
    { key: "netto_wise_sum", field: "netto_wise_sum", label: "Netto Wise", format: "number-id", align: "right", sortable: true, dash: "null" },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
  ],
  filters: [
    { kind: "select", key: "region", label: "Region", allLabel: "Semua Region", width: "w-44", optionsFrom: "available_regions" },
    { kind: "year", key: "year", label: "Tahun", allLabel: "Semua Tahun", width: "w-28" },
    { kind: "month", key: "month", label: "Bulan", allLabel: "Semua Bulan", width: "w-36" },
    { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
    { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
  ],
  preview: {
    parse: parseXlsxForPreview as unknown as (file: File) => Promise<Record<string, unknown>>,
    toMetadata: (filename, r) => ({ filename, row_count: r.rowCount, netto_wise_sum: r.nettoSum }),
    errorMetadata: (filename) => ({ filename, row_count: 0, netto_wise_sum: 0 }),
    derive: (p) => ({
      ...p,
      period_label_display: `${MONTHS[Number(p.period_month ?? 0)] ?? String(p.period_month)} ${p.period_year}`,
    }),
    card: {
      metaFields: [
        { label: "Region", field: "region", format: "text" },
        { label: "Periode", field: "period_label_display", format: "text" },
        { label: "Skema", field: "schema_version", format: "text" },
      ],
      stats: [
        { label: "Baris", field: "row_count", existingField: "existing_row_count", format: "number" },
        { label: "Netto Wise", field: "netto_wise_sum", existingField: "existing_netto_wise_sum", format: "number-id" },
      ],
    },
  },
  liveFields: ["row_count", "netto_wise_sum"],
  features: { inFlightPanel: false, bulkDelete: true, destroy: true },
  showHistoryHeading: false,
  headerButtonsAlwaysVisible: true,
  destroyConfirm: (filename) =>
    `Hapus "${filename}"?\n\nSemua data transaksi untuk periode ini juga akan dihapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua data transaksi untuk periode-periode ini juga akan dihapus.`,
}

// ---------------------------------------------------------------------------
// Monthly category descriptors — one per monthly-tracked dataset. Migrated
// datasets cross-reference their DatasetConfig (label/basePath stay in sync);
// `master_outlet` and `niq_fragrances` are page-archetype outliers with no
// DatasetConfig (deferred in M4), so their label/href are declared here.
// Note the NIQ monthly key is plural ("niq_fragrances") while its dataset key
// is singular, and `timeseries`/`ka_profitability`/dist masters are not
// monthly-tracked at all.
// ---------------------------------------------------------------------------

export const MONTHLY_CATEGORIES: Record<MonthlyCategoryKey, MonthlyCategory> = {
  b2b_sell_out: { key: "b2b_sell_out", label: b2bSellOutConfig.label, uploadsHref: b2bSellOutConfig.basePath, badge: "count" },
  master_rental: { key: "master_rental", label: masterRentalConfig.label, uploadsHref: masterRentalConfig.basePath, badge: "bang" },
  master_listing: { key: "master_listing", label: masterListingConfig.label, uploadsHref: masterListingConfig.basePath, badge: "bang" },
  market_share_b2b: { key: "market_share_b2b", label: marketShareB2bConfig.label, uploadsHref: marketShareB2bConfig.basePath, badge: "count" },
  trans_dist_so: { key: "trans_dist_so", label: transDistSoConfig.label, uploadsHref: transDistSoConfig.basePath, badge: "bang" },
  trans_dist_do: { key: "trans_dist_do", label: transDistDoConfig.label, uploadsHref: transDistDoConfig.basePath, badge: "bang" },
  master_outlet: { key: "master_outlet", label: "Master Outlet", uploadsHref: "/admin/master-outlet/uploads", badge: "bang" },
  b2b_service_level: { key: "b2b_service_level", label: b2bServiceLevelConfig.label, uploadsHref: b2bServiceLevelConfig.basePath, badge: "count" },
  b2b_sell_in: { key: "b2b_sell_in", label: b2bSellInConfig.label, uploadsHref: b2bSellInConfig.basePath, badge: "count" },
  b2b_stock: { key: "b2b_stock", label: b2bStockConfig.label, uploadsHref: b2bStockConfig.basePath, badge: "count" },
  niq_fragrances: { key: "niq_fragrances", label: "NIQ Fragrances", uploadsHref: "/admin/niq-fragrances", badge: "bang" },
  bp_opsc: { key: "bp_opsc", label: bpOpscConfig.label, uploadsHref: bpOpscConfig.basePath, badge: "count" },
}

/** Nav badge for a monthly category: undefined when complete/absent, "!" for
 *  existence-style ("bang") checks, "uploaded/total" for coverage ("count"). */
export function monthlyNavBadge(
  key: MonthlyCategoryKey,
  status: { completed: boolean; uploaded_count?: number; total_count?: number } | undefined,
): string | undefined {
  if (!status || status.completed) return undefined
  return MONTHLY_CATEGORIES[key].badge === "bang" ? "!" : `${status.uploaded_count}/${status.total_count}`
}

/** Registry map (lookup by key), mirroring `Dataset::Registry::ENTRIES`. */
// ---------------------------------------------------------------------------
// Feature #46 — Product Performance KB: price-monitoring driver
// ---------------------------------------------------------------------------

const PRICE_MONITORING_SORT_OPTIONS: SortOption[] = [
  { sort: "created_at", direction: "desc", label: "Terbaru diupload" },
  { sort: "created_at", direction: "asc", label: "Terlama diupload" },
  { sort: "period", direction: "desc", label: "Snapshot terbaru" },
  { sort: "period", direction: "asc", label: "Snapshot terlama" },
  { sort: "channel", direction: "asc", label: "Channel A–Z" },
  { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
  { sort: "status", direction: "asc", label: "Status A–Z" },
]

export const priceMonitoringConfig: DatasetConfig = {
  key: "price_monitoring",
  label: "Price Monitoring",
  basePath: "/admin/price-monitoring/uploads",
  channel: "PriceMonitoringUploadChannel",
  monthlyKey: null,
  accept: ".xlsx",
  head: {
    title: "Price Monitoring",
    description:
      "Upload dan kelola data monitoring harga shelf minimarket KA (Indomaret/Alfamart) — harga normal & promo, own vs kompetitor per kategori.",
  },
  intro: {
    prefix: "Upload file Excel ",
    strong: "Minimarket comparison price KA-IDM / KA-SAT",
    suffix: " — channel & periode dibaca otomatis dari nama file.",
  },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih file/folder",
    formatCode: "Minimarket comparison price KA-IDM _ update Mar26.xlsx",
    sheetName: "",
    periodFromA1: false,
  },
  sortOptions: PRICE_MONITORING_SORT_OPTIONS,
  columns: [
    { key: "filename", field: "filename", label: "File", role: "title" },
    { key: "channel", field: "channel", label: "Channel", format: "text", sortable: true },
    { key: "period", field: "snapshot_label", label: "Snapshot", format: "text", sortable: true },
    { key: "row_count", field: "row_count", label: "Baris", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
  ],
  filters: [
    { kind: "year", key: "year", label: "Tahun", allLabel: "Semua Tahun", width: "w-28" },
    { kind: "month", key: "month", label: "Bulan", allLabel: "Semua Bulan", width: "w-36" },
    { kind: "select", key: "channel", label: "Channel", allLabel: "Semua Channel", width: "w-32", optionsFrom: "available_channels" },
    { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
    { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
  ],
  preview: {
    parse: parsePriceMonitoringForPreview,
    // Channel + period are re-parsed authoritatively on the server from the
    // filename — the request body only needs the filename.
    toMetadata: (filename) => ({ filename }),
    card: {
      metaFields: [
        { label: "Channel", field: "channel", format: "text" },
        { label: "Snapshot", field: "snapshot_label", format: "text" },
      ],
      stats: [
        { label: "Baris tersimpan (akan diganti)", field: "existing_row_count", format: "number" },
      ],
    },
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: true, destroy: true },
  tableStyle: "plain",
  rowNoun: "baris",
  destroyConfirm: (filename) =>
    `Hapus upload "${filename}"?\n\nSemua data harga yang diimport dari file ini akan ikut terhapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua data harga untuk channel + periode ini juga akan terhapus.`,
}

// ---------------------------------------------------------------------------
// Feature #46 M2 — Product Performance KB: promo/activity + discount drivers
// ---------------------------------------------------------------------------

const PROMO_ACTIVITY_SORT_OPTIONS: SortOption[] = [
  { sort: "created_at", direction: "desc", label: "Terbaru diupload" },
  { sort: "created_at", direction: "asc", label: "Terlama diupload" },
  { sort: "source", direction: "asc", label: "Sumber A–Z" },
  { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
  { sort: "status", direction: "asc", label: "Status A–Z" },
]

export const promoActivityConfig: DatasetConfig = {
  key: "promo_activity",
  label: "Promo & Activity",
  basePath: "/admin/promo-activity/uploads",
  channel: "PromoActivityUploadChannel",
  monthlyKey: null,
  accept: ".xlsx",
  head: {
    title: "Promo & Activity",
    description:
      "Upload dan kelola kalender aktivitas marketing & trade (mkt-activity-plan / tm-activity-x-mkt) — aktivitas per brand & periode, kovariat sebab-korelasi.",
  },
  intro: {
    prefix: "Upload file Excel ",
    strong: "Marketing Plan Activity / KA - Brand Activities",
    suffix: " — jenis sumber & periode dibaca otomatis dari nama file.",
  },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih file/folder",
    formatCode: "UPDATE MARKETING PLAN ACTIVITY up to Sept '26.xlsx",
    sheetName: "",
    periodFromA1: false,
  },
  sortOptions: PROMO_ACTIVITY_SORT_OPTIONS,
  columns: [
    { key: "filename", field: "filename", label: "File", role: "title" },
    { key: "source", field: "source_label", label: "Sumber", format: "text", sortable: true },
    { key: "snapshot", field: "snapshot_label", label: "Snapshot", format: "text" },
    { key: "row_count", field: "row_count", label: "Baris", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
  ],
  filters: [
    { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
    { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
  ],
  preview: {
    parse: parsePromoActivityForPreview,
    toMetadata: (filename) => ({ filename }),
    card: {
      metaFields: [
        { label: "Sumber", field: "source_label", format: "text" },
        { label: "Snapshot", field: "snapshot_label", format: "text" },
      ],
      stats: [
        { label: "Baris tersimpan (akan diganti)", field: "existing_row_count", format: "number" },
      ],
    },
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: true, destroy: true },
  tableStyle: "plain",
  rowNoun: "baris",
  destroyConfirm: (filename) =>
    `Hapus upload "${filename}"?\n\nSemua data aktivitas yang diimport dari file ini akan ikut terhapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua data aktivitas untuk sumber ini juga akan terhapus.`,
}

const DISCOUNT_PLAN_SORT_OPTIONS: SortOption[] = [
  { sort: "created_at", direction: "desc", label: "Terbaru diupload" },
  { sort: "created_at", direction: "asc", label: "Terlama diupload" },
  { sort: "fiscal_year_label", direction: "asc", label: "Tahun fiskal A–Z" },
  { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
  { sort: "status", direction: "asc", label: "Status A–Z" },
]

export const discountPlanConfig: DatasetConfig = {
  key: "discount_plan",
  label: "Discount Plan",
  basePath: "/admin/discount-plan/uploads",
  channel: "DiscountPlanUploadChannel",
  monthlyKey: null,
  accept: ".xlsx",
  head: {
    title: "Discount Plan",
    description:
      "Upload dan kelola rencana Cosmo per akun (tm-cosmo-plan) — count produk per ACCOUNT × PIC, Max Disc & HERO SKU per akun, kovariat diskon & distribusi.",
  },
  intro: {
    prefix: "Upload file Excel ",
    strong: "KA Cosmo Plan",
    suffix: " — tahun fiskal & periode dibaca otomatis dari nama file.",
  },
  dropzone: {
    dragLine: "Drag & drop file .xlsx ke sini, atau pilih file/folder",
    formatCode: "KA Cosmo as of Q2 26.xlsx",
    sheetName: "",
    periodFromA1: false,
  },
  sortOptions: DISCOUNT_PLAN_SORT_OPTIONS,
  columns: [
    { key: "filename", field: "filename", label: "File", role: "title" },
    { key: "fiscal_year_label", field: "fiscal_year_label", label: "Tahun Fiskal", format: "text", sortable: true },
    { key: "snapshot", field: "snapshot_label", label: "Snapshot", format: "text" },
    { key: "row_count", field: "row_count", label: "Baris", format: "number", align: "right", sortable: true, zeroDash: true },
    { key: "status", field: "status", label: "Status", role: "status", sortable: true },
    { key: "created_at", field: "created_at", label: "Waktu", mobileLabel: "Diunggah", format: "date-or-created", sortable: true, wide: true },
  ],
  filters: [
    { kind: "select", key: "fiscal_year", label: "Tahun Fiskal", allLabel: "Semua Tahun", width: "w-36", optionsFrom: "available_fiscal_years" },
    { kind: "status", key: "status", label: "Status", allLabel: "Semua Status", width: "w-36" },
    { kind: "search", key: "search", label: "Cari filename", placeholder: "Cari filename…", width: "w-52" },
  ],
  preview: {
    parse: parseDiscountPlanForPreview,
    toMetadata: (filename) => ({ filename }),
    card: {
      metaFields: [
        { label: "Tahun Fiskal", field: "fiscal_year_label", format: "text" },
        { label: "Snapshot", field: "snapshot_label", format: "text" },
      ],
      stats: [
        { label: "Baris tersimpan (akan diganti)", field: "existing_row_count", format: "number" },
      ],
    },
  },
  liveFields: ["row_count"],
  features: { inFlightPanel: true, bulkDelete: true, destroy: true },
  tableStyle: "plain",
  rowNoun: "baris",
  destroyConfirm: (filename) =>
    `Hapus upload "${filename}"?\n\nSemua data discount/cosmo yang diimport dari file ini akan ikut terhapus.`,
  bulkDestroyConfirm: (count) =>
    `Hapus ${count} upload terpilih?\n\nSemua data discount/cosmo untuk tahun fiskal ini juga akan terhapus.`,
}

export const datasetRegistry: Record<string, DatasetConfig> = {
  master_rental: masterRentalConfig,
  master_listing: masterListingConfig,
  master_outlet_dist: masterOutletDistConfig,
  master_product_dist: masterProductDistConfig,
  star_master_product: starMasterProductConfig,
  trans_dist_so: transDistSoConfig,
  trans_dist_do: transDistDoConfig,
  b2b_sell_out: b2bSellOutConfig,
  b2b_service_level: b2bServiceLevelConfig,
  b2b_stock: b2bStockConfig,
  bp_opsc: bpOpscConfig,
  b2b_sell_in: b2bSellInConfig,
  market_share_b2b: marketShareB2bConfig,
  price_monitoring: priceMonitoringConfig,
  promo_activity: promoActivityConfig,
  discount_plan: discountPlanConfig,
  timeseries: timeseriesConfig,
}
